import { httpClient } from "./client";
import type { NotificationApi } from "./interfaces";
import type {
  PaginatedResponse,
  NotificationResponse,
  NotificationSend,
  NotificationRecipientOutcome,
  NotificationBatchOutcome,
  NotificationDeliveryRecoveryItem,
  NotificationDeliveryRecoveryPage,
  NotificationDeliveryResolutionRequest,
  PaginatedNotifications,
  UUID,
} from "../types";

export const notificationApi: NotificationApi = {
  list: async (params) => {
    const query = new URLSearchParams({
      limit: String(params?.limit ?? 50),
      offset: String(params?.offset ?? 0),
    });
    // Audience filters the feed to one app's domain so a shared account never
    // sees another app's notifications. Omitted ⇒ backend returns everything.
    if (params?.audience) query.set("audience", params.audience);
    const result = await httpClient.get<PaginatedNotifications>(
      `/notifications?${query.toString()}`,
    );
    return result as PaginatedResponse<NotificationResponse>;
  },

  markRead: (notificationId: UUID) =>
    httpClient.put<void>(`/notifications/${notificationId}/read`),

  markAllRead: (audience) =>
    httpClient.put<void>(
      audience
        ? `/notifications/read-all?audience=${audience}`
        : "/notifications/read-all",
    ),

  send: (payload: NotificationSend) =>
    httpClient.post<NotificationRecipientOutcome>(
      "/notifications/send",
      payload,
    ),

  sendBatch: (payloads: NotificationSend[]) =>
    httpClient.post<NotificationBatchOutcome>(
      "/notifications/send-batch",
      payloads,
    ),

  listDeliveryRecovery: (params) => {
    if (params?.statuses?.length === 0) {
      throw new Error("At least one delivery recovery status is required");
    }
    const query = new URLSearchParams({
      limit: String(params?.limit ?? 50),
    });
    if (params?.cursor) query.set("cursor", params.cursor);
    for (const status of params?.statuses ?? [
      "uncertain",
      "terminal_failure",
    ]) {
      query.append("statuses", status);
    }
    return httpClient.get<NotificationDeliveryRecoveryPage>(
      `/notifications/delivery-recovery?${query.toString()}`,
    );
  },

  resolveDelivery: (
    intentId: UUID,
    request: NotificationDeliveryResolutionRequest,
  ) =>
    httpClient.post<NotificationDeliveryRecoveryItem>(
      `/notifications/delivery-recovery/${intentId}/resolve`,
      request,
    ),
};
